// src/pages/modules/PermissionsLogPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchActivityLogs,
  clearError as clearLogError,
} from "../../store/slices/activityLogSlice";
import { Loader2, MoreVertical, Eye } from "lucide-react";
import DataTable from "../../components/common/DataTable";
import PageHeader from "../../components/common/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { usePermissions } from "hooks/usePermissions";
import usePermissionSocket from "hooks/usePermissionSocket";
import LogDataDisplay from "../../components/common/LogDataDisplay";
import { formatDateTime } from "utlis/formateDateTime";

const PermissionsLogPage = () => {
  usePermissionSocket();

  const dispatch = useDispatch();
  const { logs, pagination, isLoading, error } = useSelector(
    (state) => state.activityLog
  );
  
  const { canRead, canExport } = usePermissions("permission_log");

  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination & Search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Reset page on search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch with resource=permission filter using activity log API
  const refetch = useCallback(() => {
    if (canRead) {
      dispatch(
        fetchActivityLogs({
          page,
          page_size: pageSize,
          search: searchTerm,
          resource: "permission", // Only permission logs
        })
      );
    }
  }, [dispatch, page, pageSize, searchTerm, canRead]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearLogError());
    }
  }, [error, dispatch]);

  const getUserName = (log) => {
    if (!log.created_by) return "System";
    const { first_name = "", last_name = "", code = "" } = log.created_by || {};
    return `${first_name} ${last_name}`.trim() || code || "Unknown User";
  };

  const getUserCode = (log) => {
    return log.created_by?.code || "N/A";
  };

  const generateSummary = (log) => {
    const user = getUserName(log);
    const resource = log.resource_display || log.resource;
    const action = log.action_display || log.action_type;

    if (log.summary && log.summary.includes(user)) return log.summary;

    switch (log.action_type) {
      case "create":
        return `${user} created a permission`;
      case "update":
        return `${user} updated permission settings`;
      case "delete":
        return `${user} deleted a permission`;
      default:
        return `${user} performed "${action}" on ${resource}`;
    }
  };

  // Transform the log data to match the expected format for LogDataDisplay
  const transformLogData = (log) => {
    if (!log.data) return null;

    // If the data already has the expected format, return as is
    if (log.data.old || log.data.new || log.data.changes) {
      return log.data;
    }

    // For logs with just actor field, create a simple structure
    if (log.data.actor) {
      return {
        new: { actor: log.data.actor },
        old: {},
        changes: {},
      };
    }

    // Otherwise, create empty structure
    return {
      old: {},
      new: {},
      changes: {},
    };
  };

  const getResourceBadge = (resource) => {
    const map = { permission: "secondary" };
    return (
      <Badge variant={map[resource] || "default"}>
        {resource || "unknown"}
      </Badge>
    );
  };

  const getActionBadge = (action) => {
    const map = {
      create: "success",
      update: "default",
      delete: "destructive",
    };
    return (
      <Badge variant={map[action] || "secondary"}>
        {(action || "").toUpperCase()}
      </Badge>
    );
  };

  const columns = [
    {
      accessorKey: "resource_display",
      header: "Resource",
      cell: ({ row }) =>
        getResourceBadge(
          row.original.resource_display || row.original.resource
        ),
    },
    {
      accessorKey: "action_type",
      header: "Action",
      cell: ({ row }) => getActionBadge(row.original.action_type),
    },
    {
      accessorKey: "created_by",
      header: "Performed By",
      cell: ({ row }) => (
        <span className="font-medium">{getUserName(row.original)}</span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Timestamp",
      cell: ({ row }) => {
        return (
          <div className="text-sm">
            <div>{formatDateTime(row.original.created_at)}</div>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "View",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                setSelectedLog(row.original);
                setIsModalOpen(true);
              }}
            >
              <Eye className="mr-2 h-4 w-4" /> View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Permission Activity Logs"
        description="Track all create, update, and delete actions on permission records"
      />

      {canRead && (
        <DataTable
          data={logs}
          totalCount={pagination.count}
          columns={columns}
          searchPlaceholder="Search by resource, action, performed by..."
          showExport={false}
          showImport={false}
          onExport={() => toast.info("Export coming soon")}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={pagination.total_pages || 1}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
        />
      )}

      {/* Beautiful Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Permission Log Details</DialogTitle>
            <DialogDescription>
              {selectedLog && formatDateTime(selectedLog.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-8 py-4">
                {/* Summary Card */}
                <div className="bg-card border rounded-lg p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Performed by
                      </p>
                      <p className="text-lg font-semibold">
                        {getUserName(selectedLog)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Code: {getUserCode(selectedLog)}
                      </p>
                    </div>
                    <div className="text-right">
                      {getResourceBadge(
                        selectedLog.resource_display || selectedLog.resource
                      )}
                      <div className="mt-2">
                        {getActionBadge(selectedLog.action_type)}
                      </div>
                    </div>
                  </div>

                  {/* <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground">Summary</p>
                    <p className="mt-1 text-foreground">{generateSummary(selectedLog)}</p>
                  </div> */}
                </div>

                {/* Log Data Display - Reusable Component */}
                <LogDataDisplay logData={transformLogData(selectedLog)} />
              </div>
            </ScrollArea>
          )}

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionsLogPage;
